export interface CachedData<T> {
    data: T;
    expiresAt: number;
}

export interface CacheConfig {
    enabled: boolean;
    duration: number;   // in milliseconds
}

// Cache configuration constants
export const SECOND = 1000;  // 1 second in milliseconds
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

// Special duration value for never-expiring cache
export const NEVER_EXPIRE = -1;

// Cache configuration for specific URLs with their rules
export const CACHEABLE_URLS = {
    // GET endpoints
    '/webclock/locations_list': {
        enabled: true,
        duration: NEVER_EXPIRE, // Cache for 12 hours
    },
    '/webclock/department_list': {
        enabled: true,
        duration: NEVER_EXPIRE,  // Never expires unless forceRefresh
    },
    '/webclock/work_codes_list': {
        enabled: true,
        duration: NEVER_EXPIRE,  // Never expires unless forceRefresh
    },
    '/timeoff/timeoff_category_list': {
        enabled: true,
        duration: 12 * HOUR,  // Cache search results for 1 hour
    },

    // POST endpoints that fetch data
    '/attendance/attendance_tags': {
        enabled: true,
        duration: NEVER_EXPIRE,  // Never expires unless forceRefresh
    },
    '/attendance/online': {
        enabled: true,
        duration: 300 * SECOND,  // Cache filtered results for 30 minutes
    },
    '/timeoff/fetch_timeoff_balance': {
        enabled: true,
        duration: 1800 * SECOND,  // Cache filtered results for 30 minutes
    },
} as const;

// Default cache configuration
export const DEFAULT_CACHE_CONFIG = {
    enabled: false,
    duration: DAY,
};

export const getCacheKey = (url: string, params: any): string => {
    return `api_cache_${url}_${JSON.stringify(params)}`;
};

export const getCachedData = <T>(key: string): CachedData<T> | null => {
    try {
        const item = localStorage.getItem(key);
        if (!item) return null;
        
        const cached = JSON.parse(item) as CachedData<T>;
        
        // Never expire case
        if (cached.expiresAt === NEVER_EXPIRE) {
            return cached;
        }

        const now = Date.now();
        // Check if cache has expired
        if (now >= cached.expiresAt) {
            localStorage.removeItem(key);
            return null;
        }
        
        return cached;
    } catch {
        return null;
    }
};

export const setCachedData = <T>(key: string, data: T, duration: number = DAY): void => {
    const now = Date.now();
    const cacheData: CachedData<T> = {
        data,
        expiresAt: duration === NEVER_EXPIRE ? NEVER_EXPIRE : now + duration,
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
};

export const isCacheValid = (key: string): boolean => {
    try {
        const item = localStorage.getItem(key);
        if (!item) return false;
        
        const cached = JSON.parse(item) as CachedData<any>;
        // If expiresAt is NEVER_EXPIRE, cache is always valid
        return cached.expiresAt === NEVER_EXPIRE || Date.now() < cached.expiresAt;
    } catch {
        return false;
    }
};

export const clearCache = (keyPrefix: string = 'api_cache_'): void => {
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(keyPrefix)) {
            localStorage.removeItem(key);
        }
    }
};
